import { useState, useEffect } from "react";
import "./Dashboard.css";
import { adminAPI, appsAPI, blogsAPI, contactsAPI } from "../../services/api";
import StatsCard from "../../components/StatsCard/StatsCard";
import { Link } from "react-router-dom";

interface Stats {
  totalApps: number;
  totalBlogs: number;
  totalContacts: number;
  totalAdmins: number;
}

const Dashboard = () => {
  const [stats, setStats] = useState<Stats>({
    totalApps: 0,
    totalBlogs: 0,
    totalContacts: 0,
    totalAdmins: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchStats = async () => {
      try {
        const [appsRes, blogsRes, contactsRes, adminsRes] = await Promise.all([
          appsAPI.getAllAdmin(),
          blogsAPI.getAll(),
          contactsAPI.getAll(),
          adminAPI.getAllAdmins(),
        ]);

        if (isMounted) {
          setStats({
            totalApps: appsRes.data.data?.length || 0,
            totalBlogs:
              blogsRes.data.data?.data?.length ||
              blogsRes.data.data?.length ||
              0,
            totalContacts:
              contactsRes.data.data?.data?.length ||
              contactsRes.data.data?.length ||
              0,
            totalAdmins: adminsRes.data.data?.length || 0,
          });

          // Add a small delay to ensure smooth transition
          setTimeout(() => {
            if (isMounted) {
              setLoading(false);
            }
          }, 100);
        }
      } catch (error) {
        console.error("Error fetching stats:", error);
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchStats();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <>
      {/* Loading Screen - Always rendered, just hidden */}
      <div
        className={`dashboard-loading ${
          !loading ? "dashboard-loading--hidden" : ""
        }`}
        style={{
          opacity: loading ? 1 : 0,
          pointerEvents: loading ? "auto" : "none",
          transition: "opacity 0.3s ease-out",
        }}
      >
        <div className="loading-spinner"></div>
        <div className="loading-text">
          Loading dashboard<span className="loading-dots"></span>
        </div>
      </div>

      {/* Dashboard Content - Always rendered, just hidden */}
      <div
        className="dashboard"
        style={{
          opacity: loading ? 0 : 1,
          pointerEvents: loading ? "none" : "auto",
          transition: "opacity 0.3s ease-in",
        }}
      >
        <div className="dashboard-header">
          <h1>Dashboard Overview</h1>
          <p>Monitor your platform activity and statistics in real-time</p>
        </div>

        <div className="stats-grid">
          <StatsCard
            title="Total Apps"
            value={stats.totalApps}
            icon="📱"
            color="orange"
            link="/admin/apps"
          />
          <StatsCard
            title="Total Blog"
            value={stats.totalBlogs}
            icon="📝"
            color="blue"
            link="/admin/blog"
          />
          <StatsCard
            title="Total Contacts"
            value={stats.totalContacts}
            icon="📧"
            color="green"
            link="/admin/contacts"
          />
          <StatsCard
            title="Total Admins"
            value={stats.totalAdmins}
            icon="👥"
            color="purple"
            link="/admin/admins"
          />
        </div>

        <div className="dashboard-widgets">
          <div className="widget">
            <h3>Recent Activity</h3>
            <div className="activity-list">
              <div className="activity-item">
                <span className="activity-icon">📱</span>
                <div className="activity-content">
                  <p className="activity-title">New app added</p>
                  <p className="activity-time">2 hours ago</p>
                </div>
              </div>
              <div className="activity-item">
                <span className="activity-icon">📝</span>
                <div className="activity-content">
                  <p className="activity-title">Blog post published</p>
                  <p className="activity-time">5 hours ago</p>
                </div>
              </div>
              <div className="activity-item">
                <span className="activity-icon">📧</span>
                <div className="activity-content">
                  <p className="activity-title">New contact inquiry</p>
                  <p className="activity-time">1 day ago</p>
                </div>
              </div>
            </div>
          </div>

          <div className="widget">
            <h3>Quick Actions</h3>
            <div className="quick-actions">
              <Link to="/admin/apps" className="quick-action-btn">
                <span className="icon">➕</span>
                <span>Add New App</span>
              </Link>
              <Link to="/admin/blog" className="quick-action-btn">
                <span className="icon">✍️</span>
                <span>Write Blog Post</span>
              </Link>
              <Link to="/admin/contacts" className="quick-action-btn">
                <span className="icon">📬</span>
                <span>View Contacts</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
